import type {
  Policy,
  PolicyModeType,
} from '@kirin/api-client/contracts/policies';
import { PolicyCard } from './PolicyCard';
import type { UsePolicySettingsHook } from '../types';
import { Heading2 } from '@/components/shared/typography/Heading';
import { BodyMuted } from '@/components/shared/typography/BodyText';

interface PolicyGroupProps {
  /**
   * The display name for the group (e.g., 'Attack Detection Policies')
   */
  categoryName: string;
  /**
   * Optional description for the group
   */
  categoryDescription?: string;
  /**
   * List of policies in this group
   */
  policies: Policy[];
  /**
   * Whether the user can edit policies
   */
  canEdit: boolean;
  /**
   * Hook to fetch policy settings for a single policy
   */
  usePolicySettings: UsePolicySettingsHook;
  /**
   * Callback to toggle a policy (enabled, mode)
   */
  onTogglePolicy: (
    policy: Policy,
    enabled: boolean,
    mode?: PolicyModeType
  ) => void;
  /**
   * Callback to save policy settings
   */
  onSaveSettings: (
    policyKey: string,
    settings: Record<string, unknown>
  ) => void;
  /**
   * Callback to reset policy settings
   */
  onResetSettings: (policyKey: string) => void;
  /**
   * Function to check if a policy toggle is pending
   */
  isTogglePending: (policyKey: string) => boolean;
  /**
   * Function to check if policy settings are pending
   */
  isSettingsPending: (policyKey: string) => boolean;
  /**
   * Function to check if policy reset is pending
   */
  isResetPending: (policyKey: string) => boolean;
  /**
   * When set, this policy row plays a fade-out after YOLO is turned Off (snapshot row).
   */
  yoloExitFadePolicyKey?: string | null;
  onYoloExitFadeComplete?: () => void;
  /** The group is the whole page, so its name repeats the page title. */
  isPageTitle?: boolean;
}

export function PolicyGroup({
  categoryName,
  categoryDescription,
  policies,
  canEdit,
  usePolicySettings,
  onTogglePolicy,
  onSaveSettings,
  onResetSettings,
  isTogglePending,
  isSettingsPending,
  isResetPending,
  yoloExitFadePolicyKey = null,
  onYoloExitFadeComplete,
  isPageTitle = false,
}: PolicyGroupProps) {
  if (policies.length === 0) {
    return null;
  }

  return (
    <div className="space-y-2">
      <div className="space-y-0.5">
        <Heading2 as="h3" data-page-title={isPageTitle || undefined}>
          {categoryName}
        </Heading2>
        {categoryDescription && <BodyMuted>{categoryDescription}</BodyMuted>}
      </div>
      {/* overflow-clip, not -hidden: rounds the corners without becoming a
          scroll container, which would trap the sticky Save bar (KIR-4902). */}
      <div className="divide-y rounded-md border border-table-border overflow-clip">
        {policies.map(policy => (
          <PolicyCard
            key={
              yoloExitFadePolicyKey != null &&
              policy.key === yoloExitFadePolicyKey
                ? `${policy.key}-exit-fade`
                : policy.key
            }
            policy={policy}
            savingSettings={
              isSettingsPending(policy.key) || isResetPending(policy.key)
            }
            savingToggle={isTogglePending(policy.key)}
            canEdit={canEdit}
            usePolicySettings={usePolicySettings}
            onTogglePolicy={(enabled: boolean, mode?: PolicyModeType) =>
              onTogglePolicy(policy, enabled, mode)
            }
            onSaveSettings={onSaveSettings}
            onResetSettings={onResetSettings}
            fadeOut={policy.key === yoloExitFadePolicyKey}
            onFadeOutComplete={onYoloExitFadeComplete}
          />
        ))}
      </div>
    </div>
  );
}
